import {Button, Divider, Stack, Typography, useTheme} from '@mui/material';
import {AuthorizationsOdontoItem} from 'domain/usecases/private/authorizations-odonto/get-authorizations-odonto';
import {StyledCard} from 'presentation/components/StyledCard';
import Icon from 'presentation/icons';
import {translations} from 'presentation/translations-new';
import {textHandle} from 'presentation/utils/textHandle';
import React, {Fragment, useState} from 'react';

import {
	AuthorizationOdontoStatusEnum,
	OdontoStatus,
	cardStyleByOdontoStatus,
} from '../../constants';

import {AuthorizationCardContainer} from '../../../../components/AuthorizationCard/styles';
import {
	DownloadDocument,
	PasswordSection,
} from '../../../../components/AuthorizationCardComponents';
import {useDenialLetterOdontoHandler} from '../../hooks/useDenialLetterOdontoHandler';
import {getOdontoStatusList} from '../../utils';
import {AuthOdontoStatusDrawer} from '../AuthOdontoStatusDrawer';
import {StyledChip} from './styles';

const {authorizationCard} = translations['pt-br'].privateFlow.requestAuthorizations;
const {seeMore} = translations['pt-br'].commons;

export function AuthorizationOdontoCard(authorization: AuthorizationsOdontoItem) {
	const [selectedAuthorization, setSelectedAuthorization] =
		useState<AuthorizationsOdontoItem | null>(null);

	const theme = useTheme();
	const {borderColor, chipText, chipVariant} = cardStyleByOdontoStatus(
		theme,
		authorization?.dsStatusItemAutor as OdontoStatus,
	);

	const statusList = getOdontoStatusList(
		authorization,
		authorization?.dsStatusItemAutor as OdontoStatus,
	);
	const isDenialLetterAvailable =
		authorization?.dsStatusItemAutor === AuthorizationOdontoStatusEnum.REJECTED &&
		authorization?.nuItem &&
		authorization?.cdRestricao !== null;

	const {downloadDenialLetter, isFetchingDenialLetter} = useDenialLetterOdontoHandler({
		cdAutorizacao: authorization?.cdAutorizacao || '',
		nuItem: authorization?.nuItem || 0,
		cdRestricao: authorization?.cdRestricao || 0,
	});

	const onOpenStatusDrawer = () => {
		setSelectedAuthorization(authorization);
	};

	const onCloseStatusDrawer = () => {
		setSelectedAuthorization(null);
	};

	const isDrawerOpen = selectedAuthorization?.cdProcedimento === authorization?.cdProcedimento;
	const shouldRenderDrawer = isDrawerOpen;
	const shouldShowErrorState =
		authorization?.cdAutorizacao === null || authorization?.nuChvProcedimento === null;

	return (
		<Fragment>
			<AuthorizationCardContainer $borderColor={borderColor}>
				<Stack gap={1}>
					<Stack>
						<Typography variant="h6" fontWeight={400} color="primary">
							{textHandle(authorization?.nmProcedimento).capitalize()}
						</Typography>
					</Stack>

					<Stack direction="row" alignItems="center" justifyContent={'space-between'}>
						<StyledChip label={chipText} color={chipVariant} />

						<Button
							variant="text"
							onClick={onOpenStatusDrawer}
							endIcon={
								<Icon
									name="chevron-right-outlined"
									color={theme.palette.primary.main}
									size={20}
								/>
							}>
							{seeMore}
						</Button>
					</Stack>

					<Divider />

					<StyledCard.Content sx={{flexDirection: 'column'}} list={statusList} />
				</Stack>

				{authorization?.dsStatusItemAutor === AuthorizationOdontoStatusEnum.FINISHED ? (
					<Fragment>
						<PasswordSection
							password={authorization?.cdAutorizacao}
							validate={authorization?.dtValidadeAutorizacao}
						/>
					</Fragment>
				) : null}

				{isDenialLetterAvailable ? (
					<Fragment>
						<DownloadDocument
							label={authorizationCard.downloadLabelOdonto}
							onClick={downloadDenialLetter}
							loading={isFetchingDenialLetter}
						/>
					</Fragment>
				) : null}
			</AuthorizationCardContainer>

			{shouldRenderDrawer ? (
				<AuthOdontoStatusDrawer
					authorization={selectedAuthorization}
					openDrawer={isDrawerOpen}
					onOpenDrawer={onOpenStatusDrawer}
					onCloseDrawer={onCloseStatusDrawer}
					shouldShowErrorState={shouldShowErrorState}
				/>
			) : null}
		</Fragment>
	);
}
